import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

class SimucubeStickyAtc extends Component {
  requiredRefs = ['bar', 'price'];

  #abortController = new AbortController();

  #targetObserver = null;

  #footerObserver = null;

  #bundleObserver = null;

  #footer = null;

  #isStuck = false;

  #hiddenByFooter = false;

  #adding = false;

  #idleLabel = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartSettled, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartSettled, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#setupObservers();
    this.#syncPriceFromBundleBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#disconnectObservers();
  }

  #bundleBar() {
    return this.closest('.shopify-section')?.querySelector('simucube-bundle-bar');
  }

  #disconnectObservers() {
    this.#targetObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#bundleObserver?.disconnect();
    this.#targetObserver = null;
    this.#footerObserver = null;
    this.#bundleObserver = null;
  }

  #setupObservers() {
    this.#disconnectObservers();

    const bundleBar = this.#bundleBar();
    const target = bundleBar ?? this.closest('.shopify-section')?.querySelector('.product-details');
    if (!target) return;

    const root = getIntersectionRoot();

    this.#targetObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (!entry.isIntersecting && !this.#isStuck) {
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#show();
          }
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = false;
          this.#hide();
        }
      },
      { root },
    );
    this.#targetObserver.observe(target);

    this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (this.#footer) {
      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByFooter = true;
            this.#hide();
          } else if (!entry.isIntersecting && this.#hiddenByFooter) {
            const rect = target.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByFooter = false;
              this.#show();
            }
          }
        },
        { root, rootMargin: '200px 0px 0px 0px' },
      );
      this.#footerObserver.observe(this.#footer);
    }

    if (bundleBar) {
      this.#bundleObserver = new MutationObserver(() => this.#syncPriceFromBundleBar());
      this.#bundleObserver.observe(bundleBar, { childList: true, characterData: true, subtree: true });
    }
  }

  #show() {
    if (!scrollContainerMediaQuery.matches) return;
    if (this.#footerInView()) {
      this.#hiddenByFooter = true;
      return;
    }
    this.#isStuck = true;
    this.refs.bar.dataset.stuck = 'true';
  }

  #footerInView() {
    if (!this.#footer) return false;
    const rect = this.#footer.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  #hide() {
    this.#isStuck = false;
    this.refs.bar.dataset.stuck = 'false';
  }

  #handleBreakpointChange = () => {
    if (!scrollContainerMediaQuery.matches) {
      this.#hiddenByFooter = false;
      this.#hide();
    }
    this.#setupObservers();
  };

  #syncPriceFromBundleBar() {
    const total = this.#bundleBar()?.querySelector('[ref="total"]');
    if (!total) return;

    const text = total.textContent?.trim();
    if (text && this.refs.price.textContent !== text) {
      this.refs.price.textContent = text;
    }
  }

  handleAddToCart = () => {
    if (this.dataset.variantAvailable !== 'true') return;

    const productForm = this.closest('.shopify-section')?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`,
    );
    if (typeof productForm?.addItems !== 'function') return;

    this.#adding = true;

    const label = this.refs.addToCartLabel;
    if (label) {
      this.#idleLabel = label.textContent;
      label.textContent = this.dataset.addingLabel || 'Adding…';
    }
    this.refs.addToCartButton?.setAttribute('aria-busy', 'true');

    const bundleBar = this.#bundleBar();
    if (typeof bundleBar?.handleAddToCart === 'function' && bundleBar.dataset.variantAvailable === 'true') {
      bundleBar.handleAddToCart();
      return;
    }

    const quantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    productForm.addItems([{ variantId: this.dataset.variantId, quantity }]);
  };

  #handleCartSettled = (event) => {
    if (!this.#adding) return;

    const restore = () => {
      this.#adding = false;
      this.refs.addToCartButton?.removeAttribute('aria-busy');

      const label = this.refs.addToCartLabel;
      if (label && this.#idleLabel != null) label.textContent = this.#idleLabel;
    };

    if (event.type === StandardEvents.cartError) {
      restore();
      return;
    }

    if (event.promise instanceof Promise) {
      event.promise.then(restore, restore);
    } else {
      restore();
    }
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const productId = this.dataset.productId;
        const next = productId
          ? detail.html.querySelector(`simucube-sticky-atc[data-product-id="${productId}"]`)
          : detail.html.querySelector('simucube-sticky-atc');
        if (!next) return;

        for (const attr of next.attributes) {
          if (attr.name === 'class' || attr.name.startsWith('data-')) {
            this.setAttribute(attr.name, attr.value);
          }
        }

        const stuck = this.refs.bar.dataset.stuck;
        morph(this, next, { childrenOnly: true });
        this.refs.bar.dataset.stuck = stuck;
        this.#syncPriceFromBundleBar();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[simucube-sticky-atc] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('simucube-sticky-atc')) {
  customElements.define('simucube-sticky-atc', SimucubeStickyAtc);
}
